import React, { useState, useRef, useEffect } from 'react';
import Tooltip from '../Tooltip/Tooltip';
import './ToolbarStyleTooltip.css';

/** Default delay (ms) before showing the tooltip after hover. */
export const TOOLBAR_STYLE_TOOLTIP_OPEN_DELAY_MS = 200;

/**
 * Tooltip with the same visual style as the task edit toolbar tooltips.
 * Supports main text, optional subtitle, and optional shortcut line.
 * Applies a configurable open delay so the tooltip does not flash on quick hovers.
 *
 * @param {Object} props
 * @param {React.RefObject} props.anchorRef - Ref to the anchor element
 * @param {boolean} props.visible - Whether the tooltip is requested (e.g. anchor is hovered)
 * @param {string} props.text - Main tooltip label
 * @param {string} [props.subtitle] - Optional subtitle (e.g. explanation under the label)
 * @param {string} [props.shortcut] - Optional shortcut hint (e.g. 'Tab', 'Shift+Tab')
 * @param {boolean} [props.inactive] - If true, tooltip is styled as inactive/disabled
 * @param {'top'|'bottom'} [props.placement] - Tooltip position (top = below anchor, bottom = above anchor)
 * @param {boolean} [props.portal] - If true, render into document.body
 * @param {number} [props.openDelay] - Ms to wait before showing after visible becomes true (default: TOOLBAR_STYLE_TOOLTIP_OPEN_DELAY_MS)
 */
function ToolbarStyleTooltip({
  anchorRef,
  visible,
  text,
  subtitle = '',
  shortcut = '',
  inactive = false,
  placement = 'top',
  portal = false,
  openDelay = TOOLBAR_STYLE_TOOLTIP_OPEN_DELAY_MS
}) {
  const [delayedVisible, setDelayedVisible] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (visible) {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (openDelay <= 0) {
        setDelayedVisible(true);
      } else {
        timerRef.current = setTimeout(() => setDelayedVisible(true), openDelay);
      }
      return () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = null;
      };
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setDelayedVisible(false);
  }, [visible, openDelay]);

  /** Parse shortcut: [key] segments render as key pills, rest as plain text. No brackets = split by '+' (legacy). */
  const shortcutParts = React.useMemo(() => {
    if (!shortcut) return [];
    const bracketSegments = shortcut.split(/(\[[^\]]+\])/).filter(Boolean);
    const hasBrackets = bracketSegments.some((s) => s.startsWith('['));
    if (hasBrackets) {
      return bracketSegments.map((seg) =>
        seg.startsWith('[') ? { type: 'key', value: seg.slice(1, -1) } : { type: 'text', value: seg }
      );
    }
    return shortcut.split('+').map((part) => part.trim()).filter(Boolean).map((value) => ({ type: 'key', value }));
  }, [shortcut]);

  if (!delayedVisible || !text) return null;

  return (
    <Tooltip
      anchorRef={anchorRef}
      visible={delayedVisible}
      placement={placement}
      portal={portal}
      className={`toolbar-style-tooltip${inactive ? ' toolbar-style-tooltip-inactive' : ''}`}
    >
      {text}
      {subtitle && (
        <span className="toolbar-style-tooltip-subtitle">{subtitle}</span>
      )}
      {shortcutParts.length > 0 && (
        <span className="toolbar-style-tooltip-shortcut">
          {shortcutParts.map((part, i) => {
            if (part.type === 'key') {
              return (
                <React.Fragment key={i}>
                  {i > 0 && shortcutParts[i - 1].type === 'key' && <span className="toolbar-style-tooltip-key-sep">+</span>}
                  <span className="toolbar-style-tooltip-key">{part.value}</span>
                </React.Fragment>
              );
            }
            return <span key={i}>{part.value}</span>;
          })}
        </span>
      )}
    </Tooltip>
  );
}

export default ToolbarStyleTooltip;
